import { useState, useCallback, useEffect, useRef } from 'react';
import {
  fetchCarousels,
  createCarousel,
  updateCarousel,
  getCarousel,
  deleteCarousel,
  fetchCarouselVersion,
  getCarouselEventsUrl,
} from '../utils/carouselsApi';
import { createDefaultSlide } from '../types/slide';
import { FOREST_BG_URLS } from '../constants/backgrounds';
import { createInitialSlides } from '../constants/initialSlides';
import { fetchPublishedShowcase } from '../utils/showcaseApi';

/**
 * Starter demo metadata — persisted only after a meaningful action.
 *
 * Passive demo viewing is local-only (no DB row). If ensureCarouselPersisted()
 * fires, a meaningful action already happened (edit, template change, logo
 * change, export, etc.), so state is always 'edited'.
 */
const STARTER_DEMO_EDITED_META = {
  template_id: 'qooqoo-starter-v1',
  state: 'edited',
};

/**
 * Future: metadata for an explicit "save demo as-is / continue with demo"
 * action where the user deliberately keeps the starter demo without editing.
 * NOT USED YET — reserved for a future "Continue with Demo" button or
 * registration-time copy flow.
 *
 * const STARTER_DEMO_UNTOUCHED_META = {
 *   template_id: 'qooqoo-starter-v1',
 *   state: 'untouched',
 * };
 */

/**
 * Custom hook for carousel management
 * Handles CRUD operations, auto-save, and carousel selection.
 * 
 * Tracks `version` per carousel for optimistic concurrency control.
 * On 409 conflict: reloads latest data from server and shows a toast.
 * 
 * @param {Function} setSlides - Setter for slides state
 * @param {Function} setActiveIndex - Setter for active slide index
 * @param {Object} dirtyRef - React ref; true when user has unsaved edits
 * @param {Object} changeHintRef - React ref; human-readable hint of what changed
 * @returns {Object} Carousel state and handlers
 */
export function useCarousels(setSlides, setActiveIndex, dirtyRef, changeHintRef, authReady = true) {
  const [carousels, setCarousels] = useState([]);
  const [activeCarouselId, setActiveCarouselId] = useState(null);
  const [isLoadingCarousels, setIsLoadingCarousels] = useState(true);
  const [isCreatingCarousel, setIsCreatingCarousel] = useState(false);
  const creatingRef = useRef(false);
  const [conflictToast, setConflictToast] = useState(null);
  const [saveCount, setSaveCount] = useState(0);
  
  // Auto-save timer ref
  const saveTimerRef = useRef(null);
  // True while an async save request is in-flight (guards SSE echo)
  const savingRef = useRef(false);
  // True while loadCarousels/refreshCarousels is running (critical section —
  // blocks auto-save during the entire async data-replacement cycle)
  const refreshingRef = useRef(false);
  // Ref to access latest carousels inside autoSave without making it a dependency
  // (avoids cascading re-renders: save → carousels update → autoSave recreated → effect fires)
  const carouselsRef = useRef(carousels);
  carouselsRef.current = carousels;

  // ── Logo save refs (latest-wins protection against race conditions) ──
  // True while ANY logo save API call is in-flight (derived from counter below)
  const logoSavingRef = useRef(false);
  // In-flight counter — handles overlapping saves (Save A starts, user types,
  // Save B starts before A returns). SSE guard stays active until ALL complete.
  const logoSaveInFlightCountRef = useRef(0);
  // Monotonically incrementing counter — bumped on every handleLogoChange call.
  // Compared at response time: if seq moved forward, newer edits happened and
  // we must NOT overwrite local state with an older server response.
  const logoEditSeqRef = useRef(0);
  // Debounce timer for logo saves (coalesces rapid typing)
  const logoSaveTimerRef = useRef(null);
  // Accumulated logo changes waiting to be sent to server
  const pendingLogoRef = useRef({});
  // Stable ref for activeCarouselId — avoids stale closure in debounce callbacks.
  // A delayed logo save for carousel A must never apply to carousel B.
  const activeCarouselIdRef = useRef(activeCarouselId);
  activeCarouselIdRef.current = activeCarouselId;

  // ── Local Demo Mode ──
  // True when the user is viewing the starter demo carousel locally
  // (no persisted carousel row in DB). Set to true when fetchCarousels returns [],
  // set to false when the demo is persisted on first meaningful action.
  const localDemoRef = useRef(false);

  // Load carousels on mount (waits for auth to be ready to avoid 401 race condition)
  useEffect(() => {
    if (!authReady) return;

    const loadCarousels = async () => {
      refreshingRef.current = true;
      try {
        setIsLoadingCarousels(true);
        const data = await fetchCarousels();
        
        if (data.length === 0) {
          // ── Local Demo Mode ──
          // Show starter demo carousel from local state. No DB row created.
          // Carousel will be persisted on first meaningful edit/action.
          // Try to load the published showcase first, then fall back to hardcoded.
          let initialSlides;
          try {
            const published = await fetchPublishedShowcase();
            if (published?.slides?.length > 0) {
              initialSlides = published.slides;
            } else {
              initialSlides = createInitialSlides();
            }
          } catch {
            initialSlides = createInitialSlides();
          }
          localDemoRef.current = true;
          setCarousels([]);
          setActiveCarouselId(null);
          // Reset dirty state BEFORE setSlides to prevent phantom auto-save
          dirtyRef.current = false;
          if (saveTimerRef.current) { clearTimeout(saveTimerRef.current); saveTimerRef.current = null; }
          setSlides(initialSlides);
        } else {
          // For admin (seed) users, all carousels are returned including showcase.
          // Showcase carousels have _showcase in their document.
          // Filter for initial display — select first non-showcase carousel, or first overall.
          const nonShowcase = data.filter(c => !c._showcase);
          const firstRegular = nonShowcase.length > 0 ? nonShowcase[0] : data[0];
          setCarousels(data);
          setActiveCarouselId(firstRegular.id);
          // Reset dirty state BEFORE setSlides to prevent phantom auto-save
          dirtyRef.current = false;
          if (saveTimerRef.current) { clearTimeout(saveTimerRef.current); saveTimerRef.current = null; }
          setSlides(firstRegular.slides.length > 0 ? firstRegular.slides : createInitialSlides());
        }
      } catch (error) {
        console.error('Failed to load carousels:', error);
        // Fallback to local state
        dirtyRef.current = false;
        const initialSlides = createInitialSlides();
        setSlides(initialSlides);
      } finally {
        // Final dirty reset — catches any phantom dirty-setting that occurred
        // during the async refresh (e.g., BackgroundPicker fallback, component
        // re-mount effects). MUST come BEFORE releasing refreshingRef to prevent
        // a stale timer from slipping through the gate.
        dirtyRef.current = false;
        if (saveTimerRef.current) { clearTimeout(saveTimerRef.current); saveTimerRef.current = null; }
        refreshingRef.current = false;
        setIsLoadingCarousels(false);
      }
    };
    
    loadCarousels();
  }, [setSlides, authReady]);

  // Auto-dismiss conflict toast after 4 seconds
  useEffect(() => {
    if (conflictToast) {
      const timer = setTimeout(() => setConflictToast(null), 4000);
      return () => clearTimeout(timer);
    }
  }, [conflictToast]);

  // Save error tracking — exposed via saveError for UI notification
  const [saveError, setSaveError] = useState(null);
  // Auto-dismiss save error after 6 seconds
  useEffect(() => {
    if (saveError) {
      const t = setTimeout(() => setSaveError(null), 6000);
      return () => clearTimeout(t);
    }
  }, [saveError]);

  // Core save logic (shared by autoSave and flushSave)
  const doSave = useCallback(async (slides, carouselId) => {
    const carousel = carouselsRef.current.find(c => c.id === carouselId);
    const version = carousel?.version;

    const result = await updateCarousel(carouselId, {
      slides,
      ...(version !== undefined ? { version } : {}),
      ...(changeHintRef?.current ? { hint: changeHintRef.current } : {}),
    });

    // Handle version conflict (409 or _conflict)
    if (result && result.conflict) {
      dirtyRef.current = false;
      const serverCarousel = result.serverCarousel;
      if (serverCarousel) {
        const sv = result.server_version || serverCarousel.version || '?';
        const yv = result.your_version || version || '?';
        console.warn(`[autoSave] Version conflict (local v${yv} → server v${sv}) — synced with server`);
        setSlides(serverCarousel.slides || []);

        // Preserve local logo fields if logo editing is active or pending
        const logoActive = logoSavingRef.current || logoSaveTimerRef.current || Object.keys(pendingLogoRef.current).length > 0;
        setCarousels(prev => prev.map(c => {
          if (c.id !== carouselId) return c;
          const merged = { ...c, ...serverCarousel, updated_at: serverCarousel.updated_at };
          if (logoActive) {
            // Keep current local logo values — don't let server overwrite active typing
            const LOGO_KEYS = ['logo_title', 'logo_subtitle', 'logo_enabled', 'logo_size', 'logo_invert', 'logo_is_dark', 'logo_asset_id', 'logo_url'];
            for (const k of LOGO_KEYS) {
              if (c[k] !== undefined) merged[k] = c[k];
            }
          }
          return merged;
        }));
        setConflictToast('Synced — merged with a newer save');
      } else {
        console.warn('[autoSave] Version conflict — refetching from server');
        try {
          const fresh = await getCarousel(carouselId);
          setSlides(fresh.slides || []);
          const logoActive = logoSavingRef.current || logoSaveTimerRef.current || Object.keys(pendingLogoRef.current).length > 0;
          setCarousels(prev => prev.map(c => {
            if (c.id !== carouselId) return c;
            const merged = { ...c, ...fresh };
            if (logoActive) {
              const LOGO_KEYS = ['logo_title', 'logo_subtitle', 'logo_enabled', 'logo_size', 'logo_invert', 'logo_is_dark', 'logo_asset_id', 'logo_url'];
              for (const k of LOGO_KEYS) {
                if (c[k] !== undefined) merged[k] = c[k];
              }
            }
            return merged;
          }));
        } catch (fetchErr) {
          console.warn('[autoSave] Refetch after conflict failed:', fetchErr.message);
        }
        setConflictToast('Synced — merged with a newer save');
      }
      return true; // saved (conflict resolved)
    }

    // Normal success
    dirtyRef.current = false;
    if (changeHintRef) changeHintRef.current = null;
    setSaveCount(prev => prev + 1);
    setSaveError(null); // clear any previous save error
    if (result && result.version !== undefined) {
      setCarousels(prev => prev.map(c =>
        c.id === carouselId
          ? { ...c, slides, version: result.version, updated_at: result.updated_at || new Date().toISOString() }
          : c
      ));
    } else {
      setCarousels(prev => prev.map(c =>
        c.id === carouselId ? { ...c, slides, updated_at: new Date().toISOString() } : c
      ));
    }
    return true; // saved
  }, [setSlides, dirtyRef]);

  /**
   * Ensure the local demo carousel is persisted to the backend.
   * Called on first meaningful action (edit, template change, logo change, export, etc.)
   * Returns the new carousel object, or null on failure.
   */
  const ensureCarouselPersisted = useCallback(async (slides) => {
    if (!localDemoRef.current) {
      // Already persisted or not in local demo mode
      return carouselsRef.current.find(c => c.id === activeCarouselIdRef.current) || null;
    }

    try {
      const slidesToPersist = slides || slidesRef.current || createInitialSlides();
      const newCarousel = await createCarousel(null, slidesToPersist, STARTER_DEMO_EDITED_META);

      localDemoRef.current = false;
      dirtyRef.current = false;
      if (saveTimerRef.current) { clearTimeout(saveTimerRef.current); saveTimerRef.current = null; }
      setActiveCarouselId(newCarousel.id);
      setCarousels([newCarousel]);

      console.info('[useCarousels] Local demo persisted as carousel:', newCarousel.id);
      return newCarousel;
    } catch (error) {
      console.error('[useCarousels] Failed to persist local demo:', error);
      return null;
    }
  }, []);

  // Auto-save function with version tracking + 409 conflict handling
  const autoSave = useCallback((slides, carouselId) => {
    if (slides.length === 0) return;
    
    // DIRTY CHECK — skip entirely if no user edits (prevents echo saves
    // from polling, conflict reloads, carousel switches, initial loads, etc.)
    if (!dirtyRef.current) return;
    
    // Clear existing timer
    if (saveTimerRef.current) {
      clearTimeout(saveTimerRef.current);
    }
    
    // Set new timer for auto-save (1 second debounce)
    saveTimerRef.current = setTimeout(async () => {
      // SECOND dirty check — catches races where dirtyRef was reset during
      // the 1s debounce (e.g., by refreshCarousels, loadCarousels, or
      // carousel switch). Without this, stale timers can bump the version
      // on carousels the user never actually edited.
      // Also blocks during active refresh (critical section lock).
      if (!dirtyRef.current || refreshingRef.current) {
        saveTimerRef.current = null;
        return;
      }

      // ── Local Demo Mode: persist on first edit ──
      if (!carouselId && localDemoRef.current) {
        savingRef.current = true;
        try {
          await ensureCarouselPersisted(slides);
        } catch (error) {
          console.warn('[autoSave] Failed to persist local demo:', error.message);
          setSaveError(`Save failed: ${error.message}`);
        } finally {
          savingRef.current = false;
          saveTimerRef.current = null;
        }
        return;
      }

      if (!carouselId) {
        saveTimerRef.current = null;
        return;
      }
      
      // Keep saveTimerRef truthy during the async request so SSE handler
      // knows a save is in-flight and won't trigger an echo-fetch.
      // We clear it only after the entire callback completes.
      savingRef.current = true;
      try {
        await doSave(slides, carouselId);
      } catch (error) {
        console.warn('[autoSave] Save failed:', error.message);
        // Retry once after 2s (handles Neon cold start timeouts)
        try {
          await new Promise(r => setTimeout(r, 2000));
          if (dirtyRef.current) {
            await doSave(slides, carouselId);
            console.info('[autoSave] Retry succeeded');
          }
        } catch (retryError) {
          console.warn('[autoSave] Retry also failed:', retryError.message);
          // Show error to user — don't silently swallow persistent failures
          setSaveError(`Save failed: ${retryError.message}`);
        }
      } finally {
        savingRef.current = false;
        saveTimerRef.current = null;
      }
    }, 1000);
  }, [doSave, dirtyRef, ensureCarouselPersisted]);

  /**
   * Immediately flush any pending save (before carousel switch).
   * Returns true if save succeeded or nothing to save, false on failure.
   */
  const flushSave = useCallback(async (slides, carouselId) => {
    // Cancel any pending debounce timer
    if (saveTimerRef.current) {
      clearTimeout(saveTimerRef.current);
      saveTimerRef.current = null;
    }

    // ── Local Demo Mode: persist on flush if dirty ──
    if (!carouselId && localDemoRef.current && dirtyRef.current && slides.length > 0) {
      savingRef.current = true;
      try {
        const result = await ensureCarouselPersisted(slides);
        return !!result;
      } catch (error) {
        console.warn('[flushSave] Failed to persist local demo:', error.message);
        setSaveError(`Save failed: ${error.message}`);
        return false;
      } finally {
        savingRef.current = false;
      }
    }

    // Nothing to save
    if (!dirtyRef.current || !carouselId || slides.length === 0) return true;

    savingRef.current = true;
    try {
      await doSave(slides, carouselId);
      return true;
    } catch (error) {
      console.warn('[flushSave] Save failed before carousel switch:', error.message);
      setSaveError(`Save failed: ${error.message}`);
      // Even on failure, update local cache with current slides so they aren't lost
      setCarousels(prev => prev.map(c =>
        c.id === carouselId ? { ...c, slides } : c
      ));
      return false;
    } finally {
      savingRef.current = false;
    }
  }, [doSave, dirtyRef]);

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      if (saveTimerRef.current) {
        clearTimeout(saveTimerRef.current);
      }
      if (logoSaveTimerRef.current) {
        clearTimeout(logoSaveTimerRef.current);
      }
    };
  }, []);

  // =========================================================================
  // SSE — real-time push notifications for remote carousel changes
  //
  // Opens an EventSource to GET /api/carousels/{id}/events.
  // The server pushes { carousel_id, version } on every successful save.
  // When a new version arrives, fetches the full carousel and updates state
  // (without triggering an echo-save thanks to the dirty-flag mechanism).
  //
  // Guards:
  //   - Closes connection when tab is hidden (saves server resources)
  //   - Re-opens + immediate version check on tab re-focus
  //   - Skips update when a save is pending (debounce timer active)
  //   - Falls back to one-shot version poll on tab refocus (catches events
  //     missed while the connection was closed)
  // =========================================================================
  useEffect(() => {
    if (!activeCarouselId) return;

    let eventSource = null;
    let closed = false; // tracks intentional close (cleanup / visibility)

    const handleRemoteUpdate = async (serverVersion, hint) => {
      // Skip if a slide save is pending (debounce timer) or in-flight (async request)
      if (saveTimerRef.current || savingRef.current) return;
      // Skip if a logo save is pending, in-flight, or user has uncommitted logo edits
      if (logoSaveTimerRef.current || logoSavingRef.current || Object.keys(pendingLogoRef.current).length > 0) return;

      const localCarousel = carouselsRef.current.find(c => c.id === activeCarouselId);
      const localVersion = localCarousel?.version;

      // Only act if we have a local version and server is strictly newer
      if (localVersion !== undefined && serverVersion > localVersion) {
        try {
          const fresh = await getCarousel(activeCarouselId);
          setSlides(fresh.slides || []);
          setCarousels(prev => prev.map(c =>
            c.id === activeCarouselId ? { ...c, ...fresh } : c
          ));
          const message = hint
            ? `Synced — ${hint}`
            : 'Synced — changes from another session';
          setConflictToast(message);
        } catch (err) {
          console.warn('[SSE] Failed to fetch updated carousel:', err.message);
        }
      }
    };

    const openSSE = () => {
      if (closed) return;
      const url = getCarouselEventsUrl(activeCarouselId);
      eventSource = new EventSource(url);

      eventSource.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (data.version) {
            handleRemoteUpdate(data.version, data.hint);
          }
        } catch (err) {
          console.warn('[SSE] Failed to parse event:', err.message);
        }
      };

      eventSource.onerror = () => {
        // EventSource auto-reconnects on error (built-in browser behavior).
        // No manual retry logic needed — the browser handles exponential backoff.
      };
    };

    const closeSSE = () => {
      if (eventSource) {
        eventSource.close();
        eventSource = null;
      }
    };

    const handleVisibility = async () => {
      if (document.visibilityState === 'visible') {
        // Tab became visible — reopen SSE + catch up on missed events
        openSSE();
        // One-shot version check to catch anything missed while hidden
        try {
          const serverInfo = await fetchCarouselVersion(activeCarouselId);
          await handleRemoteUpdate(serverInfo.version);
        } catch {
          // Silent — non-critical
        }
      } else {
        // Tab hidden — close connection to save server resources
        closeSSE();
      }
    };

    // Start SSE connection
    openSSE();
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      closed = true;
      closeSSE();
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [activeCarouselId, setSlides]);

  // Ref to track current slides for flushSave during carousel switch
  const slidesRef = useRef([]);

  // Handle carousel selection
  // 1. Flush any pending save for the CURRENT carousel (don't lose edits!)
  // 2. Switch to local cache instantly (no lag)
  // 3. Fetch fresh slides + version from server in background
  const handleCarouselSelect = useCallback(async (id) => {
    if (id === activeCarouselId) return;
    
    const carousel = carousels.find(c => c.id === id);
    if (carousel) {
      // STEP 1: Flush any pending save for the current carousel before switching.
      // This prevents data loss when the user edits and quickly switches.
      if (dirtyRef.current && activeCarouselId) {
        await flushSave(slidesRef.current, activeCarouselId);
      } else {
        // No pending save — just cancel the timer
        if (saveTimerRef.current) { clearTimeout(saveTimerRef.current); saveTimerRef.current = null; }
      }

      // STEP 1b: Flush any pending logo save for the current carousel
      if (logoSaveTimerRef.current) {
        clearTimeout(logoSaveTimerRef.current);
        logoSaveTimerRef.current = null;
      }
      if (Object.keys(pendingLogoRef.current).length > 0 && activeCarouselId) {
        const accumulated = { ...pendingLogoRef.current };
        pendingLogoRef.current = {};
        const currentCarousel = carouselsRef.current.find(c => c.id === activeCarouselId);
        if (currentCarousel) {
          try {
            await updateCarousel(activeCarouselId, {
              ...accumulated,
              version: currentCarousel.version,
            });
          } catch (err) {
            console.warn('[handleCarouselSelect] Logo flush failed:', err.message);
          }
        }
      }

      // STEP 2: Reset dirty state — switching carousels is not a user edit
      dirtyRef.current = false;

      // Instant switch from local cache — no network wait
      setActiveCarouselId(id);
      setSlides(carousel.slides.length > 0 ? carousel.slides : [createDefaultSlide(0, 1, FOREST_BG_URLS.length)]);
      setActiveIndex(0);

      // STEP 3: Background — fetch fresh slides + version from server.
      // Updates BOTH version AND slides in local cache, so switching back shows latest data.
      getCarousel(id).then(fresh => {
        // Update the carousels cache with full fresh data (slides + version)
        setCarousels(prev => prev.map(c =>
          c.id === id ? { ...c, slides: fresh.slides || c.slides, version: fresh.version } : c
        ));
        // If the user hasn't edited yet (still clean), also update the displayed slides
        // This ensures the view reflects the server's latest without overwriting user edits
        if (!dirtyRef.current && fresh.slides && fresh.slides.length > 0) {
          setSlides(fresh.slides);
        }
      }).catch(() => {
        // Silent — local data is already displayed, version will sync on next successful save
      });
    }
  }, [activeCarouselId, carousels, setSlides, setActiveIndex, flushSave]);

  // Handle carousel creation
  const handleCarouselCreate = useCallback(async () => {
    if (creatingRef.current) return; // guard against double-click
    creatingRef.current = true;
    setIsCreatingCarousel(true);
    try {
      // If in local demo mode, discard the unpersisted demo
      if (localDemoRef.current) {
        localDemoRef.current = false;
      }
      const newSlides = [createDefaultSlide(0, 1, FOREST_BG_URLS.length)];
      const newCarousel = await createCarousel(null, newSlides);
      setCarousels(prev => [newCarousel, ...prev]);
      setActiveCarouselId(newCarousel.id);
      // Reset dirty state — new carousel from server, not a user edit
      dirtyRef.current = false;
      if (saveTimerRef.current) { clearTimeout(saveTimerRef.current); saveTimerRef.current = null; }
      setSlides(newCarousel.slides);
      setActiveIndex(0);
    } catch (error) {
      console.error('Failed to create carousel:', error);
      alert('Failed to create carousel');
    } finally {
      creatingRef.current = false;
      setIsCreatingCarousel(false);
    }
  }, [setSlides, setActiveIndex]);

  // Handle carousel rename
  const handleCarouselRename = useCallback(async (id, newName) => {
    try {
      const result = await updateCarousel(id, { name: newName });
      if (result && result.conflict) {
        // Conflict during rename — reload
        setConflictToast('Synced — merged with a newer save');
        const fresh = await getCarousel(id);
        setCarousels(prev => prev.map(c =>
          c.id === id ? { ...c, ...fresh } : c
        ));
        return;
      }
      setCarousels(prev => prev.map(c => 
        c.id === id
          ? { ...c, name: newName, version: result?.version ?? c.version }
          : c
      ));
    } catch (error) {
      console.error('Failed to rename carousel:', error);
      alert('Failed to rename carousel');
    }
  }, []);

  // Handle carousel deletion
  const handleCarouselDelete = useCallback(async (id) => {
    try {
      await deleteCarousel(id);
      setCarousels(prev => {
        const next = prev.filter(c => c.id !== id);
        // If we deleted the active carousel, switch to the first remaining one
        if (id === activeCarouselId && next.length > 0) {
          setActiveCarouselId(next[0].id);
          // Reset dirty state — loading different carousel, not a user edit
          dirtyRef.current = false;
          if (saveTimerRef.current) { clearTimeout(saveTimerRef.current); saveTimerRef.current = null; }
          setSlides(next[0].slides.length > 0 ? next[0].slides : [createDefaultSlide(0, 1, FOREST_BG_URLS.length)]);
          setActiveIndex(0);
        }
        return next;
      });
    } catch (error) {
      console.error('Failed to delete carousel:', error);
      alert('Failed to delete carousel');
    }
  }, [activeCarouselId, setSlides, setActiveIndex]);

  // Handle template change — direct API call + optimistic local update
  const handleTemplateChange = useCallback(async (templateId) => {
    // ── Local Demo Mode: persist first ──
    let carouselId = activeCarouselIdRef.current;
    if (!carouselId && localDemoRef.current) {
      const newCarousel = await ensureCarouselPersisted(slidesRef.current);
      if (!newCarousel) return;
      carouselId = newCarousel.id;
    }
    if (!carouselId) return;

    const carousel = carouselsRef.current.find(c => c.id === carouselId);
    if (!carousel) return;

    // Optimistic local update (instant visual feedback)
    setCarousels(prev =>
      prev.map(c => c.id === carouselId ? { ...c, template: templateId } : c)
    );

    try {
      const result = await updateCarousel(carouselId, {
        template: templateId,
        version: carousel.version,
      });
      // Update version from server response (avoid future conflicts)
      if (result && !result.conflict) {
        setCarousels(prev =>
          prev.map(c => c.id === carouselId ? { ...c, ...result, template: templateId } : c)
        );
      }
    } catch (err) {
      console.error('Failed to update template:', err);
      // Revert optimistic update on failure
      setCarousels(prev =>
        prev.map(c => c.id === carouselId ? { ...c, template: carousel.template } : c)
      );
    }
  }, [ensureCarouselPersisted]);

  // Handle logo change — optimistic local update + debounced API call
  // Uses latest-wins protection to prevent server responses from overwriting
  // newer local typing. See logoEditSeqRef / logoSavingRef / pendingLogoRef.
  const handleLogoChange = useCallback((updates) => {
    let carouselId = activeCarouselIdRef.current;

    // ── Local Demo Mode: persist first, then apply logo changes ──
    if (!carouselId && localDemoRef.current) {
      // Optimistic local update first (instant preview feedback)
      setCarousels(prev =>
        prev.length > 0 ? prev.map(c => ({ ...c, ...updates })) : prev
      );
      // Persist the demo, then save logo changes
      ensureCarouselPersisted(slidesRef.current).then(newCarousel => {
        if (!newCarousel) return;
        const capturedSeq = logoEditSeqRef.current;
        const accumulated = { ...updates, ...pendingLogoRef.current };
        pendingLogoRef.current = {};
        updateCarousel(newCarousel.id, {
          ...accumulated,
          version: newCarousel.version,
        }).catch(err => console.warn('[handleLogoChange] Logo save after demo persist failed:', err.message));
      });
      return;
    }

    if (!carouselId) return;

    // Track edit sequence — response handler compares this to detect newer edits
    logoEditSeqRef.current += 1;

    // Accumulate all pending changes (title + subtitle + enabled + url + …)
    pendingLogoRef.current = { ...pendingLogoRef.current, ...updates };

    // Optimistic local update (instant preview feedback)
    setCarousels(prev =>
      prev.map(c => c.id === carouselId ? { ...c, ...updates } : c)
    );

    // Debounce the API call (coalesce rapid changes like typing)
    if (logoSaveTimerRef.current) clearTimeout(logoSaveTimerRef.current);
    logoSaveTimerRef.current = setTimeout(async () => {
      logoSaveTimerRef.current = null;

      // Capture state at debounce-fire time
      const capturedCarouselId = carouselId;
      const capturedSeq = logoEditSeqRef.current;
      const accumulated = { ...pendingLogoRef.current };
      pendingLogoRef.current = {};

      // Guard: if carousel changed since the edit, skip this save
      if (activeCarouselIdRef.current !== capturedCarouselId) return;

      const carousel = carouselsRef.current.find(c => c.id === capturedCarouselId);
      if (!carousel) return;

      logoSaveInFlightCountRef.current += 1;
      logoSavingRef.current = true;
      try {
        const result = await updateCarousel(capturedCarouselId, {
          ...accumulated,
          version: carousel.version,
        });

        if (result && !result.conflict) {
          setCarousels(prev =>
            prev.map(c => {
              if (c.id !== capturedCarouselId) return c;

              // If user typed MORE since this save started, preserve local logo fields
              const hasNewerEdits = logoEditSeqRef.current !== capturedSeq;
              const hasPending = Object.keys(pendingLogoRef.current).length > 0;

              if (hasNewerEdits || hasPending) {
                // Only take version/updated_at from server — preserve local logo values
                return {
                  ...c,
                  version: result.version ?? c.version,
                  updated_at: result.updated_at ?? c.updated_at,
                };
              }

              // No newer edits — safe to apply full server result
              return { ...c, ...result };
            })
          );
        } else if (result && result.conflict) {
          // Conflict: preserve local logo fields, absorb version from server
          const serverCarousel = result.serverCarousel;
          if (serverCarousel) {
            const LOGO_KEYS = ['logo_title', 'logo_subtitle', 'logo_enabled', 'logo_size', 'logo_invert', 'logo_is_dark', 'logo_asset_id', 'logo_url'];
            setCarousels(prev =>
              prev.map(c => {
                if (c.id !== capturedCarouselId) return c;
                const merged = { ...c, version: serverCarousel.version, updated_at: serverCarousel.updated_at };
                // Keep current local logo values — they are newer
                for (const k of LOGO_KEYS) {
                  if (c[k] !== undefined) merged[k] = c[k];
                }
                return merged;
              })
            );
            // Retry the logo save with fresh version
            logoSaveInFlightCountRef.current += 1;
            try {
              await updateCarousel(capturedCarouselId, {
                ...accumulated,
                ...(Object.keys(pendingLogoRef.current).length > 0 ? pendingLogoRef.current : {}),
                version: serverCarousel.version,
              });
            } catch (retryErr) {
              console.warn('[logo] Conflict retry failed:', retryErr.message);
            } finally {
              logoSaveInFlightCountRef.current = Math.max(0, logoSaveInFlightCountRef.current - 1);
              logoSavingRef.current = logoSaveInFlightCountRef.current > 0;
            }
          }
        }
      } catch (err) {
        console.error('Failed to update logo:', err);
      } finally {
        logoSaveInFlightCountRef.current = Math.max(0, logoSaveInFlightCountRef.current - 1);
        logoSavingRef.current = logoSaveInFlightCountRef.current > 0;
      }
    }, 600);
  }, []); // No deps — uses refs for everything (stable function identity)

  return {
    // State
    carousels,
    activeCarouselId,
    isLoadingCarousels,
    isCreatingCarousel,
    conflictToast,
    saveCount,
    saveError,
    slidesRef,
    isLocalDemo: localDemoRef,
    
    // Handlers
    handleCarouselSelect,
    handleCarouselCreate,
    handleCarouselRename,
    handleCarouselDelete,
    handleTemplateChange,
    handleLogoChange,
    autoSave,
    ensureCarouselPersisted,
    dismissSaveError: () => setSaveError(null),
    dismissConflictToast: () => setConflictToast(null),
    refreshCarousels: useCallback(async () => {
      refreshingRef.current = true;
      try {
        setIsLoadingCarousels(true);
        // Reset dirty state + cancel pending saves before replacing slides
        // Prevents stale dirtyRef from pre-auth edits causing phantom saves
        dirtyRef.current = false;
        if (saveTimerRef.current) { clearTimeout(saveTimerRef.current); saveTimerRef.current = null; }
        const data = await fetchCarousels();
        if (data.length === 0) {
          // ── Local Demo Mode ──
          localDemoRef.current = true;
          setCarousels([]);
          setActiveCarouselId(null);
          setSlides(createInitialSlides());
        } else {
          localDemoRef.current = false;
          setCarousels(data);
          // Preserve the currently active carousel if it still exists
          setActiveCarouselId(prev => {
            if (prev && data.some(c => c.id === prev)) return prev;
            const nonShowcase = data.filter(c => !c._showcase);
            return (nonShowcase.length > 0 ? nonShowcase[0] : data[0]).id;
          });
        }
      } catch (error) {
        console.error('Failed to refresh carousels:', error);
        setCarousels([]);
        setSlides(createInitialSlides());
      } finally {
        // Final dirty reset — catches any phantom dirty-setting that occurred
        // during the async refresh (e.g., component re-mount effects, stale
        // timer callbacks). MUST come BEFORE releasing refreshingRef.
        dirtyRef.current = false;
        if (saveTimerRef.current) { clearTimeout(saveTimerRef.current); saveTimerRef.current = null; }
        refreshingRef.current = false;
        setIsLoadingCarousels(false);
      }
    }, [setSlides, setActiveIndex]),
  };
}
